import { useState } from 'react';

export function ValuationFinalCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    numberOfUnits: '',
    estimatedValue: '',
    timeline: '',
    message: ''
  });

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'message') {
      if (value.length <= 500) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setCharCount(value.length);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5qoadc98t1oshnre0d2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          numberOfUnits: '',
          estimatedValue: '',
          timeline: '',
          message: ''
        });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
  };

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Get Your Professional Property Valuation Today
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Now that you understand how apartment building valuation works, let our experienced team provide you with an accurate, no-obligation valuation of your property. We buy apartment buildings in any condition and can close quickly with cash.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="text-white">
            <h3 className="text-3xl font-bold mb-6">Why Choose Our Valuation Service?</h3>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">Fast & Accurate Valuations</h4>
                  <p className="text-white/80">Receive a professional valuation within 24-48 hours using all three primary methods</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">No Obligation</h4>
                  <p className="text-white/80">Get your valuation with zero pressure to sell. Use it for your own planning purposes</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">Cash Offers Available</h4>
                  <p className="text-white/80">If you decide to sell, we can make a cash offer and close in as little as 7-14 days</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">Any Condition Accepted</h4>
                  <p className="text-white/80">We buy properties as-is, so you don't need to make any repairs or improvements</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">Experienced Investors</h4>
                  <p className="text-white/80">Our team has valued and purchased hundreds of apartment buildings nationwide</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
              <h4 className="text-2xl font-bold mb-4">Call Us Now</h4>
              <a 
                href="tel:5551234567" 
                className="text-4xl font-bold hover:text-teal-200 transition-colors inline-block"
              >
                (555) 123-4567
              </a>
              <p className="text-white/80 mt-3">Available 7 days a week, 8am-8pm</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Request Your Free Valuation</h3>
            <p className="text-gray-600 mb-6">Fill out the form below and we'll contact you within 24 hours</p>
            
            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="valuation-cta-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="propertyAddress" className="block text-sm font-medium text-gray-700 mb-1">
                  Property Address *
                </label>
                <input
                  type="text"
                  id="propertyAddress"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="123 Main St, City, State ZIP"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="numberOfUnits" className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Units *
                  </label>
                  <input
                    type="number"
                    id="numberOfUnits"
                    name="numberOfUnits"
                    value={formData.numberOfUnits}
                    onChange={handleChange}
                    required
                    min="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="12"
                  />
                </div>
                <div>
                  <label htmlFor="estimatedValue" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Estimated Value
                  </label>
                  <input
                    type="text"
                    id="estimatedValue"
                    name="estimatedValue"
                    value={formData.estimatedValue}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="$2,000,000"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-1">
                  When Are You Looking to Sell? *
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select timeline</option>
                  <option value="ASAP">As Soon As Possible</option>
                  <option value="1-3 months">Within 1-3 Months</option>
                  <option value="3-6 months">Within 3-6 Months</option>
                  <option value="6-12 months">Within 6-12 Months</option>
                  <option value="Just exploring">Just Exploring Options</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Information
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your property, any specific questions, or concerns..."
                />
                <p className="text-xs text-gray-500 mt-1">{charCount}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={submitStatus === 'submitting'}
                className="w-full bg-teal-600 text-white py-3 px-6 rounded-md font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {submitStatus === 'submitting' ? 'Submitting...' : 'Get My Free Valuation Now'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md">
                  Thank you! We'll contact you within 24 hours with your property valuation.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
                  Something went wrong. Please try again or call us at (555) 123-4567.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}